import { Show, createEffect, createMemo, createSignal, onCleanup, untrack, type JSX } from "solid-js";
import type { DiffModel } from "@fastdiff/worker";
import { buildRows, type NavHunk, type Row } from "../nav-hunks";
import type { TokensByLine, Token } from "@fastdiff/highlight";
import { prepare, layout } from "@chenglou/pretext";
import { Lines } from "../lines";
import { DiffLine } from "./DiffLine";
import { Minimap } from "./Minimap";
import { PaneHeader } from "./PaneHeader";
import { VirtualList } from "./VirtualList";
import type { FontSize } from "../stores/view";
import type { Match } from "../search";
import type { SearchMatchRange } from "./DiffLine";

interface SplitViewProps {
  model: DiffModel;
  leftLines: Lines;
  rightLines: Lines;
  /** Visual height per row in CSS pixels when wrap is off. Defaults to 19.5. */
  rowHeight?: number;
  /** Optional per-line syntax tokens for the left text (indexed by source line). */
  leftTokens?: TokensByLine;
  /** Optional per-line syntax tokens for the right text. */
  rightTokens?: TokensByLine;
  /** Detected/assigned language for left pane (passed to PaneHeader badge). */
  leftLang?: string;
  /** Detected/assigned language for right pane (passed to PaneHeader badge). */
  rightLang?: string;
  /** When true, soft-wrap is on and rows take variable heights. */
  wrap?: boolean;
  /** Active font-size preference; drives line-height for variable-row heights. */
  fontSize?: FontSize;
  /** When true, syntax highlighting is in flight; an overlay spinner is shown. */
  highlighting?: boolean;
  /** Non-equal hunks with their row ranges; used for nav scroll + outline. */
  navHunks?: readonly NavHunk[];
  /** Index into navHunks of the currently-focused nav hunk. */
  currentHunkIndex?: number;
  /** Search matches across both panes. */
  matches?: readonly Match[];
  /** Index into matches; the corresponding line is scrolled into view. */
  currentMatchIndex?: number;
  /** Reports timings for JS-side phases that can't be measured in a Worker. */
  onTimingMs?: (key: "buildRows" | "wrapLayout" | "minimap", ms: number) => void;
  /** When true, the minimap column is rendered to the right of the diff panes. */
  minimap?: boolean;
}

// Must match theme.css (font-size + line-height-code) and .fd-line grid layout.
const FONT_SIZE_PX: Record<FontSize, number> = { S: 12, M: 13, L: 15 };
const LINE_HEIGHT_RATIO = 1.5;
// .fd-line: padding 0 8px + grid columns 50px (line-num) + 16px (marker) = 82px gutter.
const GUTTER_PX = 82;

function resolveMonoFontFamily(): string {
  if (typeof document === "undefined") return "ui-monospace, monospace";
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue("--fd-font-mono")
    .trim();
  return v || "ui-monospace, monospace";
}

export function SplitView(props: SplitViewProps): JSX.Element {
  const fontSize = (): FontSize => props.fontSize ?? "M";
  const fontPx = () => FONT_SIZE_PX[fontSize()];
  const lineHeightPx = () => fontPx() * LINE_HEIGHT_RATIO;
  const rowHeight = () => props.rowHeight ?? lineHeightPx();

  const rows = createMemo(() => {
    const t0 = performance.now();
    const result = buildRows(props.model, props.leftLines, props.rightLines);
    const ms = performance.now() - t0;
    props.onTimingMs?.("buildRows", ms);
    return result;
  });
  const hasChanges = createMemo(() => props.model.hunks.some((h) => h.kind !== "equal"));

  const lookupTokens = (
    pile: TokensByLine | undefined,
    idx: number | null,
  ): ReadonlyArray<Token> | undefined => {
    if (!pile || idx == null) return undefined;
    return pile[idx];
  };

  let leftScroller: HTMLDivElement | undefined;
  let rightScroller: HTMLDivElement | undefined;

  // Width of the text column (post-gutter) inside the scroller, observed via
  // ResizeObserver on the left pane scroller. Both panes are 1fr 1fr in a
  // grid, so the right pane has the same width and can share this signal.
  const [textColWidth, setTextColWidth] = createSignal(0);

  // Scroll position and scroller height signals, published for the Minimap.
  const [scrollTop, setScrollTop] = createSignal(0);
  const [scrollerHeight, setScrollerHeight] = createSignal(0);

  const observeScroller = (el: HTMLDivElement, side: "left" | "right") => {
    if (side === "left") {
      leftScroller = el;
      if (typeof ResizeObserver !== "undefined") {
        const ro = new ResizeObserver((entries) => {
          for (const e of entries) {
            setTextColWidth(Math.max(0, e.contentRect.width - GUTTER_PX));
            setScrollerHeight(e.contentRect.height);
          }
        });
        ro.observe(el);
        onCleanup(() => ro.disconnect());
        setTextColWidth(Math.max(0, el.clientWidth - GUTTER_PX));
        setScrollerHeight(el.clientHeight);
      }
    } else {
      rightScroller = el;
    }
  };

  // When wrap is on, measure each row's wrapped height via pretext and take
  // the per-row max of left/right so both panes stay aligned 1:1. When wrap
  // is off, the signal stays undefined so VirtualList uses its fixed-itemHeight
  // path. The compute is deferred via setTimeout so the spinner gets to paint
  // before the (potentially long) synchronous prepare/layout pass blocks the
  // main thread.
  const [rowHeights, setRowHeights] = createSignal<readonly number[] | undefined>(undefined);
  const [computing, setComputing] = createSignal(false);

  createEffect(() => {
    if (!props.wrap) {
      setRowHeights(undefined);
      setComputing(false);
      return;
    }
    const w = textColWidth();
    const fs = fontSize();
    const items = rows();
    if (w <= 0 || items.length === 0) {
      setRowHeights(undefined);
      setComputing(false);
      return;
    }
    setComputing(true);
    const handle = setTimeout(() => {
      const t0 = performance.now();
      const font = `${FONT_SIZE_PX[fs]}px ${resolveMonoFontFamily()}`;
      const lh = FONT_SIZE_PX[fs] * LINE_HEIGHT_RATIO;
      const out = new Array<number>(items.length);
      for (let i = 0; i < items.length; i++) {
        const r = items[i] as Row;
        const lText = r.left.text;
        const rText = r.right.text;
        const lH = lText.length === 0
          ? lh
          : layout(prepare(lText, font, { whiteSpace: "pre-wrap" }), w, lh).height;
        const rH = rText.length === 0
          ? lh
          : layout(prepare(rText, font, { whiteSpace: "pre-wrap" }), w, lh).height;
        out[i] = Math.max(lH, rH, lh);
      }
      const wrapLayoutMs = performance.now() - t0;
      props.onTimingMs?.("wrapLayout", wrapLayoutMs);
      setRowHeights(out);
      setComputing(false);
    }, 0);
    onCleanup(() => clearTimeout(handle));
  });

  const rowOffsets = createMemo<readonly number[] | null>(() => {
    const h = rowHeights();
    if (!h) return null;
    const o = new Array<number>(h.length + 1);
    o[0] = 0;
    for (let i = 0; i < h.length; i++) o[i + 1] = (o[i] as number) + (h[i] as number);
    return o;
  });

  const topOfRow = (i: number): number => {
    const o = rowOffsets();
    return o ? (o[i] as number) : i * rowHeight();
  };

  const totalContentHeight = (): number => {
    const offs = rowOffsets();
    if (offs) return offs[offs.length - 1] ?? 0;
    return rows().length * rowHeight();
  };

  // Show the minimap only when the user actually has somewhere to scroll —
  // a single-screen diff doesn't need an overview. Before the first scroller
  // measurement (clientHeight=0, e.g. happy-dom tests) we show it
  // optimistically; the next layout tick will hide it if content fits.
  const showMinimap = (): boolean => {
    if (!props.minimap) return false;
    const vh = scrollerHeight();
    if (vh <= 0) return true;
    return totalContentHeight() > vh;
  };

  // Center the target row range vertically in the viewport. For multi-row
  // hunks the hunk's midpoint is centered; for a single-row search match the
  // row's midpoint is centered. In tests where clientHeight is 0, the target
  // degenerates to (top + height/2).
  const scrollToRow = (rowIdx: number, rowEndIdx?: number) => {
    if (!leftScroller) return;
    const scroller = leftScroller;
    untrack(() => {
      const top = topOfRow(rowIdx);
      const height = rowEndIdx !== undefined
        ? topOfRow(rowEndIdx) - top
        : rowHeight();
      const vh = scroller.clientHeight;
      scroller.scrollTop = Math.max(0, top - vh / 2 + height / 2);
    });
  };

  // ナビ操作 (currentHunkIndex 変化) のみで scroll。リサイズや wrap toggle で
  // rowOffsets が変わっても自動 scroll しない (untrack で遮断)。
  createEffect(() => {
    const nh = props.navHunks?.[props.currentHunkIndex ?? 0];
    if (!nh) return;
    scrollToRow(nh.rowStart, nh.rowEnd);
  });

  // Build per-row, per-side match index. Each row gets its left/right match
  // ranges (text-relative); the currently-focused match is flagged isCurrent.
  const matchesByRowSide = createMemo<{
    left:  Map<number, SearchMatchRange[]>;
    right: Map<number, SearchMatchRange[]>;
  }>(() => {
    const left  = new Map<number, SearchMatchRange[]>();
    const right = new Map<number, SearchMatchRange[]>();
    const all = props.matches ?? [];
    const curIdx = props.currentMatchIndex ?? 0;
    const cur = all[curIdx] ?? null;
    for (const m of all) {
      const bucket = m.side === "left" ? left : right;
      const arr = bucket.get(m.rowIndex);
      const isCurrent = cur !== null
        && m.rowIndex === cur.rowIndex
        && m.side === cur.side
        && m.start === cur.start
        && m.end === cur.end;
      const piece: SearchMatchRange = { start: m.start, end: m.end, isCurrent };
      if (arr) arr.push(piece);
      else bucket.set(m.rowIndex, [piece]);
    }
    return { left, right };
  });

  const matchesFor = (side: "left" | "right", rowIdx: number): SearchMatchRange[] | undefined => {
    return matchesByRowSide()[side].get(rowIdx);
  };

  // When currentMatchIndex changes, scroll the corresponding row into view.
  createEffect(() => {
    const ms = props.matches ?? [];
    const idx = props.currentMatchIndex ?? 0;
    const m = ms[idx];
    if (!m) return;
    scrollToRow(m.rowIndex);
  });

  const overlays = createMemo(() => {
    const nh = props.navHunks?.[props.currentHunkIndex ?? 0];
    if (!nh) return [];
    return [{ rowStart: nh.rowStart, rowEnd: nh.rowEnd, class: "fd-hunk-current" }];
  });

  // Both panes share the same row heights, so mirror scrollTop directly.
  // scrollLeft is also mirrored so horizontal scroll on long lines stays in
  // lock-step (no-op when wrap=on because scrollLeft is always 0). The
  // tolerance check is what breaks the ping-pong: when dst's scroll setter
  // fires its own onScroll, src and dst are already equal so the reverse
  // call no-ops without needing a re-entry flag.
  const syncFrom = (src: HTMLDivElement, dst: HTMLDivElement) => {
    const targetTop = src.scrollTop;
    const targetLeft = src.scrollLeft;
    const sameTop = Math.abs(dst.scrollTop - targetTop) < 0.5;
    const sameLeft = Math.abs(dst.scrollLeft - targetLeft) < 0.5;
    if (sameTop && sameLeft) return;
    if (!sameTop) dst.scrollTop = targetTop;
    if (!sameLeft) dst.scrollLeft = targetLeft;
  };

  return (
    <div class="fd-splitview">
      <div class="fd-splitview-headers">
        <PaneHeader
          name={props.model.meta.leftName ?? ""}
          bytes={props.model.meta.leftBytes}
          encoding={props.model.encoding.left}
          lang={props.leftLang}
        />
        <PaneHeader
          name={props.model.meta.rightName ?? ""}
          bytes={props.model.meta.rightBytes}
          encoding={props.model.encoding.right}
          lang={props.rightLang}
        />
      </div>
      <Show
        when={hasChanges()}
        fallback={
          <div class="fd-splitview-identical">
            <p>ファイルは同一です</p>
            <p class="fd-splitview-identical-meta">
              {props.leftLines.count} 行 ・ 差分なし
            </p>
          </div>
        }
      >
        <div class={`fd-splitview-panes ${showMinimap() ? "fd-splitview-with-minimap" : ""}`}>
          <Show when={computing() || props.highlighting}>
            <div class="fd-splitview-overlay" aria-live="polite" aria-busy="true">
              <div class="fd-spinner" />
            </div>
          </Show>
          <div data-pane="left" class="fd-splitview-pane">
            <VirtualList
              items={rows()}
              itemHeight={rowHeight()}
              itemHeights={rowHeights()}
              overlays={overlays()}
              overscan={6}
              onScrollerRef={(el) => observeScroller(el, "left")}
              onScroll={() => {
                if (leftScroller && rightScroller) syncFrom(leftScroller, rightScroller);
                if (leftScroller) setScrollTop(leftScroller.scrollTop);
              }}
              renderItem={(row, idx) => (
                <DiffLine
                  lineNumber={row.left.lineNumber}
                  kind={row.left.kind}
                  text={row.left.text}
                  inlineOps={row.left.inlineOps}
                  tokens={lookupTokens(props.leftTokens, row.left.sourceLine)}
                  matches={matchesFor("left", idx)}
                />
              )}
            />
          </div>
          <div data-pane="right" class="fd-splitview-pane">
            <VirtualList
              items={rows()}
              itemHeight={rowHeight()}
              itemHeights={rowHeights()}
              overlays={overlays()}
              overscan={6}
              onScrollerRef={(el) => observeScroller(el, "right")}
              onScroll={() => { if (leftScroller && rightScroller) syncFrom(rightScroller, leftScroller); }}
              renderItem={(row, idx) => (
                <DiffLine
                  lineNumber={row.right.lineNumber}
                  kind={row.right.kind}
                  text={row.right.text}
                  inlineOps={row.right.inlineOps}
                  tokens={lookupTokens(props.rightTokens, row.right.sourceLine)}
                  matches={matchesFor("right", idx)}
                />
              )}
            />
          </div>
          <Show when={showMinimap()}>
            <Minimap
              rows={rows()}
              viewportTop={scrollTop}
              viewportHeight={scrollerHeight}
              totalContentHeight={totalContentHeight}
              navHunks={props.navHunks ?? []}
              currentHunkIndex={() => props.currentHunkIndex ?? 0}
              tokens={{ left: props.leftTokens, right: props.rightTokens }}
              fontSize={fontSize}
              onJumpTo={(top) => { if (leftScroller) leftScroller.scrollTop = top; }}
              onWheel={(dy) => { if (leftScroller) leftScroller.scrollTop += dy; }}
              onTimingMs={(ms) => props.onTimingMs?.("minimap", ms)}
            />
          </Show>
        </div>
      </Show>
    </div>
  );
}
